import { apiCall, API_CONFIG } from './api';

const normalizeStoryPagination = (pagination = {}, limit = 20) => {
  const currentPage = pagination.currentPage ?? pagination.page ?? 1;
  const totalPages = Math.max(
    1,
    pagination.totalPages ?? pagination.pages ?? 1,
  );
  const totalCount = pagination.totalCount ?? pagination.total ?? 0;
  return {
    currentPage,
    totalPages,
    totalCount,
    limit: pagination.limit ?? limit,
    hasNext:
      pagination.hasNext !== undefined
        ? pagination.hasNext
        : currentPage < totalPages,
    hasPrev:
      pagination.hasPrev !== undefined
        ? pagination.hasPrev
        : currentPage > 1,
  };
};

export const storyAPI = {
  // Fetch Active stories
  fetchActiveStories: async () => {
    try {
      const response = await apiCall(
        API_CONFIG.ENDPOINTS.STORY_ACTIVE,
        { method: 'GET' }
      );

      if (response.success) {
        return {
          success: true,
          data: response.data,
          activeCount: response.activeCount,
          expiredCount: response.expiredCount,
          msg: response.msg || 'Active stories fetched successfully'
        };
      } else {
        return {
          success: false,
          error: response.msg || response.error || 'Failed to fetch active stories',
          data: []
        };
      }
    } catch (error) {
      console.error('Error fetching active stories:', error);
      return {
        success: false,
        error: 'Error fetching active stories: ' + error.message,
        data: []
      };
    }
  },

  // Fetch expired stories
  fetchExpiredStories: async (page = 1, limit = 20, filter = {}) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        filter: JSON.stringify(filter)
      });

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.STORY_EXPIRED}?${queryParams}`,
        { method: 'GET' }
      );

      if (response.success) {
        const payload = response.data || {};
        return {
          success: true,
          data: {
            stories: payload.stories || [],
            pagination: normalizeStoryPagination(payload.pagination, limit),
          },
          msg: response.msg || 'Expired stories fetched successfully'
        };
      } else {
        return {
          success: false,
          error: response.msg || response.error || 'Failed to fetch expired stories',
          data: {
            stories: [],
            pagination: {
              page: 1,
              limit: limit,
              total: 0,
              pages: 1
            }
          }
        };
      }
    } catch (error) {
      console.error('Error fetching expired stories:', error);
      return {
        success: false,
        error: 'Error fetching expired stories: ' + error.message,
        data: {
          stories: [],
          pagination: {
            page: 1,
            limit: limit,
            total: 0,
            pages: 1
          }
        }
      };
    }
  },

  // Create new story
  createStory: async (formData) => {
    try {
      const data = await apiCall(API_CONFIG.ENDPOINTS.STORY_CREATE, {
        method: 'POST',
        body: formData
      });

      if (data.success) {
        return {
          success: true,
          data: data.data,
          msg: data.msg || 'Story created successfully'
        };
      } else {
        return {
          success: false,
          error: data.msg || data.error || 'Failed to create story',
          errorCode: data.errorCode
        };
      }
    } catch (error) {
      console.error('Error creating story:', error);
      return {
        success: false,
        error: 'Error creating story: ' + error.message
      };
    }
  },

  // Update story
  updateStory: async (storyId, formData) => {
    try {
      const data = await apiCall(`${API_CONFIG.ENDPOINTS.STORY_UPDATE}${storyId}`, {
        method: 'PUT',
        body: formData
      });

      if (data.success) {
        return {
          success: true,
          data: data.data,
          msg: data.msg || 'Story updated successfully'
        };
      } else {
        return {
          success: false,
          error: data.msg || data.error || 'Failed to update story',
          errorCode: data.errorCode
        };
      }
    } catch (error) {
      console.error('Error updating story:', error);
      return {
        success: false,
        error: 'Error updating story: ' + error.message
      };
    }
  },

  // Add collection to story
  addCollectionToStory: async (storyId, formData) => {
    try {
      const data = await apiCall(`${API_CONFIG.ENDPOINTS.STORY_BASE}/${storyId}/collections`, {
        method: 'POST',
        body: formData
      });

      if (data.success) {
        return {
          success: true,
          data: data.data,
          msg: data.msg || 'Collection added successfully'
        };
      } else {
        return {
          success: false,
          error: data.msg || data.error || 'Failed to add collection',
          errorCode: data.errorCode
        };
      }
    } catch (error) {
      console.error('Error adding collection:', error);
      return {
        success: false,
        error: 'Error adding collection: ' + error.message
      };
    }
  },

  // Update collection in story
  updateCollectionInStory: async (storyId, collectionId, payload) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.STORY_BASE}/${storyId}/collections/${collectionId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          msg: response.msg || 'Collection updated successfully'
        };
      } else {
        return {
          success: false,
          error: response.msg || response.error || 'Failed to update collection',
          errorCode: response.errorCode
        };
      }
    } catch (error) {
      console.error('Error updating collection:', error);
      return {
        success: false,
        error: 'Error updating collection: ' + error.message
      };
    }

  },

  reorderStories: async (payload) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.STORY_REORDER, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          msg: response.msg || 'Stories reordered successfully'
        };
      }

      return {
        success: false,
        error: response.msg || response.error || 'Failed to reorder stories',
        errorCode: response.errorCode
      };
    } catch (error) {
      console.error('Error reordering stories:', error);
      return {
        success: false,
        error: 'Error reordering stories: ' + error.message
      };
    }
  },

  // Repost story
  repostStory: async (storyId) => {
    try {
      const formData = new FormData();
      formData.append('postedAt', 'repost');

      const result = await storyAPI.updateStory(storyId, formData);

      if (result.success) {
        return {
          success: true,
          data: result.data,
          msg: 'Story reposted successfully'
        };
      } else {
        return {
          success: false,
          error: result.error || 'Failed to repost story'
        };
      }
    } catch (error) {
      console.error('Error reposting story:', error);
      return {
        success: false,
        error: 'Error reposting story: ' + error.message
      };
    }
  },

  deleteStory: async (storyId) => {
    try {
      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.STORY_BASE}/${storyId}`,
        { method: 'DELETE' }
      );

      if (response.success) return { success: true };
      return { success: false, error: response.msg || response.error || 'Failed to delete Story' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  deleteCollection: async (storyId, collectionId) => {
    try {
      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.STORY_BASE}/${storyId}/collections/${collectionId}`,
        { method: 'DELETE' }
      );

      if (response.success) return { success: true };
      return { success: false, error: response.msg || response.error || 'Failed to delete collection' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  },
};
